/** Client-side image helpers for character portraits — mirrors the book-cover
 * approach (downscale to a data URL stored in the DB), so no file-storage layer is
 * needed. Produces a square, optimized JPEG suitable for avatars everywhere. */

export const PORTRAIT_SIZE = 512;

/** Read a File into an HTMLImageElement. */
export async function loadImageFile(file: File): Promise<HTMLImageElement> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Could not read the image file."));
    reader.readAsDataURL(file);
  });
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("That file is not a readable image."));
    el.src = dataUrl;
  });
}

/**
 * Crop a source region of an image to a square and downscale to PORTRAIT_SIZE,
 * returning a JPEG data URL. `region` is in the source image's own pixel space; if
 * omitted, the largest centered square is used.
 */
export function cropToPortrait(
  img: HTMLImageElement,
  region?: { sx: number; sy: number; size: number }
): string {
  const natural = Math.min(img.naturalWidth, img.naturalHeight);
  const r = region ?? { sx: (img.naturalWidth - natural) / 2, sy: (img.naturalHeight - natural) / 2, size: natural };
  const canvas = document.createElement("canvas");
  canvas.width = PORTRAIT_SIZE;
  canvas.height = PORTRAIT_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable.");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, r.sx, r.sy, r.size, r.size, 0, 0, PORTRAIT_SIZE, PORTRAIT_SIZE);
  return canvas.toDataURL("image/jpeg", 0.85);
}

/** Convenience: File → centered-square portrait data URL (no manual crop). */
export async function fileToPortrait(file: File): Promise<string> {
  return cropToPortrait(await loadImageFile(file));
}

/**
 * Downscale an image File to a wide JPEG data URL (long edge <= maxEdge), keeping
 * aspect ratio. Used for location hero images and gallery shots stored in the DB.
 */
export async function fileToWide(file: File, maxEdge = 1600): Promise<string> {
  const img = await loadImageFile(file);
  let { naturalWidth: width, naturalHeight: height } = img;
  if (width > maxEdge || height > maxEdge) {
    const scale = maxEdge / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable.");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.82);
}
